import React, { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import {
  ComprehensiveMatches,
  Provider,
  Recommendation,
  EstimatedCosts,
  QuickAssessmentData,
} from '../../services/smart-matching.service'
import { Package } from '../../services/packages.service'
import { casesService } from '../../services/cases.service'
import { useComprehensiveMatches } from '../../hooks/useSmartMatching'
import { generateCaseNumber } from '../../utils/helpers'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../LoadingSpinner'

interface ProviderDashboardStepProps {
  caseData: any
  assessmentData: QuickAssessmentData
  selectedPackage: Package
  onComplete?: (selectedProviders: string[]) => void
  onSkip?: () => void
}

interface ProviderCardProps {
  provider: Provider
  type: 'ambulance' | 'ustadz' | 'burial'
  onBook: (provider: Provider) => void
  isBooked?: boolean
  isLoading?: boolean
}

const ProviderCard: React.FC<ProviderCardProps> = ({ provider, type, onBook, isBooked, isLoading }) => {
  const getProviderIcon = (type: string) => {
    switch (type) {
      case 'ambulance': return '🚑'
      case 'ustadz': return '👳'
      case 'burial': return '⛰️'
      default: return '🏢'
    }
  }

  const getProviderTitle = (type: string) => {
    switch (type) {
      case 'ambulance': return 'Ambulance Service'
      case 'ustadz': return 'Ustadz (Islamic Scholar)'
      case 'burial': return 'Burial Ground'
      default: return 'Service Provider'
    }
  }

  const getStatusColor = (rating: number) => {
    if (rating >= 4.5) return 'bg-green-100 text-green-800'
    if (rating >= 3.5) return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="text-2xl mr-3">{getProviderIcon(type)}</div>
          <div>
            <h3 className="font-semibold text-lg text-neutral-900">
              {provider.companyName || getProviderTitle(type)}
            </h3>
            {provider.avgRating && (
              <div className="flex items-center mt-1">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(provider.avgRating)}`}>
                  ⭐ {provider.avgRating.toFixed(1)}
                </div>
                {provider.reviewCount && (
                  <span className="text-sm text-neutral-500 ml-2">
                    ({provider.reviewCount} reviews)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {isBooked && (
          <Badge variant="success" className="bg-green-600">
            Booked
          </Badge>
        )}
      </div>

      <div className="space-y-3">
        {provider.distance && (
          <div className="flex items-center text-sm">
            <svg className="w-4 h-4 text-neutral-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 0L10 18.9l4.95-4.95a7 7 0 00-9.9-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span className="text-neutral-600">
              {provider.distance}km away
            </span>
          </div>
        )}

        {provider.estimatedArrival && type === 'ambulance' && (
          <div className="flex items-center text-sm">
            <svg className="w-4 h-4 text-neutral-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 001 1h1a1 1 0 001-1v-4z" clipRule="evenodd" />
            </svg>
            <span className="text-neutral-600">
              ETA: {provider.estimatedArrival}
            </span>
          </div>
        )}

        {provider.availableSlots && (
          <div className="flex items-center text-sm">
            <svg className="w-4 h-4 text-neutral-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6zM4 8a1 1 0 011-1h1a1 1 0 011 1H4zm1 4a1 1 0 10-2 0h1a1 1 0 112 0H4zm12-6a1 1 0 011-1h1a1 1 0 011 1h-1zm-1 4a1 1 0 10-2 0h1a1 1 0 112 0h-1z" clipRule="evenodd" />
            </svg>
            <span className="text-neutral-600">
              {provider.availableSlots} slots available
            </span>
          </div>
        )}

        {provider.canPerformJanazah && type === 'ustadz' && (
          <div className="flex items-center text-sm">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 01-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L10 10.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-green-600 font-medium">
              Performs Janazah prayers
            </span>
          </div>
        )}

        {provider.availablePlots && type === 'burial' && (
          <div className="flex items-center text-sm">
            <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1H4a1 1 0 100 2h1a1 1 0 011 1v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1a1 1 0 110-2h1a1 1 0 001-1V4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-600 font-medium">
              {provider.availablePlots} plots available
            </span>
          </div>
        )}

        {provider.muslimSectionAvailable && type === 'burial' && (
          <div className="flex items-center text-sm">
            <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 001-1v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-blue-600 font-medium">
              Muslim section available
            </span>
          </div>
        )}

        {provider.expertise && provider.expertise.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {provider.expertise.slice(0, 3).map((expertise, index) => (
              <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                {expertise}
              </span>
            ))}
          </div>
        )}

        {provider.facilities && provider.facilities.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {provider.facilities.slice(0, 3).map((facility, index) => (
              <span key={index} className="px-2 py-1 bg-gray-50 text-gray-700 text-xs rounded-full">
                {facility}
              </span>
            ))}
          </div>
        )}
      </div>

      <Button
        variant={isBooked ? 'success' : 'default'}
        onClick={() => onBook(provider)}
        disabled={isLoading}
        className="w-full mt-4"
      >
        {isLoading ? (
          <span className="flex items-center">
            <LoadingSpinner size="sm" className="mr-2" />
            Processing...
          </span>
        ) : isBooked ? (
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Selected
          </span>
        ) : (
          <span className="flex items-center">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Select Provider
          </span>
        )}
      </Button>
    </div>
  )
}

const ProviderDashboardStep: React.FC<ProviderDashboardStepProps> = ({
  caseData,
  assessmentData,
  selectedPackage,
  onComplete,
  onSkip
}) => {
  const [matches, setMatches] = useState<ComprehensiveMatches | null>(null)
  const [bookedProviders, setBookedProviders] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isBooking, setIsBooking] = useState(false)
  const [previewCaseNumber] = useState<string>(generateCaseNumber())
  const [error, setError] = useState<string | null>(null)

  const comprehensiveMatchesMutation = useComprehensiveMatches()
  const { user } = useAuth()

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setIsLoading(true)
        const result = await comprehensiveMatchesMutation.mutateAsync({
          location: assessmentData.currentLocation,
          urgencyLevel: assessmentData.urgencyLevel,
          preferredBurialArea: assessmentData.preferredBurialArea,
          specialRequirements: [],
          selectedPackage: selectedPackage ? {
            id: selectedPackage.id,
            name: selectedPackage.name,
            price: selectedPackage.price,
            includes: selectedPackage.includes || [],
            excludes: selectedPackage.excludes || []
          } : undefined,
        })
        setMatches(result)
      } catch (error) {
        console.error('Failed to fetch provider matches:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchMatches()
  }, [assessmentData.currentLocation, assessmentData.urgencyLevel, assessmentData.preferredBurialArea])

  // Case creation is now handled by FuneralWorkflowContainer only

  const handleBookProvider = async (provider: Provider) => {
    if (!user) {
      setError('User authentication required. Please log in.')
      return
    }

    setIsBooking(true)
    setError(null)

    try {
      // Toggle provider selection (add if not selected, remove if already selected)
      const newBookedProviders = new Set(bookedProviders)
      if (newBookedProviders.has(provider.id)) {
        newBookedProviders.delete(provider.id)
        setBookedProviders(newBookedProviders)
      } else {
        newBookedProviders.add(provider.id)
        setBookedProviders(newBookedProviders)
      }

      // Show success message
      const isSelected = newBookedProviders.has(provider.id)
      const action = isSelected ? 'selected' : 'deselected'
      console.log(`Provider ${action}:`, provider.companyName)

    } catch (error: any) {
      console.error('Failed to handle provider selection:', error)
      setError(error.message || 'Failed to update provider selection. Please try again.')
    } finally {
      setIsBooking(false)
    }
  }

  
const handleComplete = async () => {
    // Case creation is now handled by FuneralWorkflowContainer
    // Pass the selected provider IDs to the parent component
    const selectedProviderIds = Array.from(bookedProviders)
    onComplete?.(selectedProviderIds)
  }

  const handleSkip = async () => {
    // Case creation is now handled by FuneralWorkflowContainer
    onSkip?.()
  }

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full">
            <LoadingSpinner size="lg" />
          </div>
          <h1 className="text-3xl font-bold text-neutral-900">
            Finding Best Providers for You
          </h1>
          <p className="text-lg text-neutral-600">
            We're matching your requirements with top-rated Islamic funeral service providers...
          </p>
        </div>
      </div>
    )
  }

  if (!matches) {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-red-600">
            Unable to Load Provider Matches
          </h1>
          <p className="text-lg text-neutral-600">
            Please try again later or contact our support team.
          </p>
        </div>
      </div>
    )
  }

  // Error display
  if (error) {
    return (
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-2xl font-bold text-red-600 mb-2">
              Error Occurred
            </h1>
            <p className="text-lg text-red-700 mb-4">
              {error}
            </p>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <span className="text-2xl">✅</span>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900">
          Provider Matches Found
        </h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          We've found the best providers for your funeral arrangements. You can select providers now, and they'll be booked when your case is created.
        </p>
        {bookedProviders.size > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-green-800 font-medium">
              {bookedProviders.size} provider{bookedProviders.size > 1 ? 's' : ''} selected for booking
            </p>
          </div>
        )}
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center justify-center space-x-2">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Quick Assessment</span>
        </div>
        <div className="w-8 h-0.5 bg-green-600"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Package Selection</span>
        </div>
        <div className="w-8 h-0.5 bg-green-600"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Deceased Information</span>
        </div>
        <div className="w-8 h-0.5 bg-green-600"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            4
          </div>
          <span className="ml-2 text-sm font-medium text-primary-600">Provider Matches</span>
        </div>
      </div>

      {/* Case Summary */}
      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">Case Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-2xl mb-1">📋</div>
              <div className="font-medium text-green-900">Case Created</div>
              <div className="text-sm text-green-700 mt-1">
                #{previewCaseNumber}
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-2xl mb-1">📦</div>
              <div className="font-medium text-blue-900">Package</div>
              <div className="text-sm text-blue-700 mt-1">
                {selectedPackage.name} - RM{selectedPackage.price.toLocaleString()}
              </div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="text-2xl mb-1">⚡</div>
              <div className="font-medium text-orange-900">Urgency</div>
              <div className="text-sm text-orange-700 mt-1">
                {assessmentData.urgencyLevel}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Recommendations */}
      {matches.recommendations.length > 0 && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-neutral-900 mb-4">
              Our Recommendations
            </h2>
            <div className="space-y-3">
              {matches.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                      rec.priority === 'high' ? 'bg-red-500' : rec.priority === 'medium' ? 'bg-orange-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="font-medium text-neutral-900">
                      {rec.provider}
                    </div>
                    <div className="text-sm text-neutral-600 mt-1">
                      {rec.reason}
                    </div>
                    {rec.estimatedArrival && (
                      <div className="text-sm text-neutral-600">
                        <strong>ETA:</strong> {rec.estimatedArrival}
                      </div>
                    )}
                    <div className="mt-2">
                      <Badge variant="outline" className="text-xs">
                        {rec.action}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Cost Summary */}
      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            Estimated Costs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Ambulance Service:</span>
                <span className="font-medium">RM {matches.estimatedCosts.ambulance}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Ustadz Services:</span>
                <span className="font-medium">RM {matches.estimatedCosts.ustadz}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Burial Ground:</span>
                <span className="font-medium">RM {matches.estimatedCosts.burialGround}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-600">Additional Services:</span>
                <span className="font-medium">RM {matches.estimatedCosts.estimatedAdditional}</span>
              </div>
            </div>
            <div className="border-t pt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total Estimated Cost:</span>
                <span className="text-xl font-bold text-primary-600">
                  RM {matches.estimatedCosts.total.toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-neutral-500 mt-1">
                *Final costs may vary based on specific requirements
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Provider Matches */}
      <div className="space-y-8">
        {/* Ambulance Providers */}
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            🚑 Ambulance Services ({matches.ambulance.matchedCount} matches found)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.ambulance.providers.map((provider) => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                type="ambulance"
                onBook={handleBookProvider}
                isBooked={bookedProviders.has(provider.id)}
                isLoading={isBooking}
              />
            ))}
          </div>
        </div>

        {/* Ustadz Providers */}
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            👳 Ustadz (Islamic Scholars) ({matches.ustadz.matchedCount} matches found)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.ustadz.providers.map((provider) => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                type="ustadz"
                onBook={handleBookProvider}
                isBooked={bookedProviders.has(provider.id)}
                isLoading={isBooking}
              />
            ))}
          </div>
        </div>

        {/* Burial Grounds */}
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-4">
            ⛰️ Burial Grounds ({matches.burialGrounds.matchedCount} matches found)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {matches.burialGrounds.providers.map((provider) => (
              <ProviderCard
                key={provider.id}
                provider={provider}
                type="burial"
                onBook={handleBookProvider}
                isBooked={bookedProviders.has(provider.id)}
                isLoading={isBooking}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 001-1v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Next Steps
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <ol className="list-decimal list-inside space-y-1">
                <li>Book your preferred providers from the options above</li>
                <li>Prepare required documents (death certificate, medical certificate, ID)</li>
                <li>Our team will coordinate with all providers for smooth service delivery</li>
                <li>You'll receive real-time updates on your case progress</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Skip Option Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800 mb-2">
              Skip Provider Booking - What This Means
            </h3>
            <div className="text-sm text-blue-700 space-y-1">
              <p>• Your case will be created and saved with all provider matching results</p>
              <p>• You can review and book providers anytime from your case dashboard</p>
              <p>• All provider information and pricing will be preserved for your reference</p>
              <p>• No commitment required - you retain full control over provider selection</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={() => window.history.back()}
          disabled={isBooking}
        >
          Go Back
        </Button>

        <div className="flex items-center space-x-4">
          {/* Skip Option */}
          <div className="text-center">
            <Button
              variant="outline"
              onClick={handleSkip}
              disabled={isBooking}
              className="min-w-44"
            >
              Skip for Later
            </Button>
            <p className="text-xs text-neutral-500 mt-1">
              Save providers & book later
            </p>
          </div>

          {/* Complete Option */}
          <div className="text-center">
            <Button
              variant="islamic"
              onClick={handleComplete}
              disabled={isBooking}
              className="min-w-48"
            >
              {isBooking ? 'Processing...' : 'Complete & Book Now'}
            </Button>
            <p className="text-xs text-neutral-500 mt-1">
              Book selected providers
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProviderDashboardStep